import { Suspense } from "react"
import { Container } from "@mui/system"
import { Card, Grid, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Breadcrumb, BreadcrumbLink, StandardBreadcrumbs } from "../../../shared/components/links/StandardBreadcrumbs"
import { getAdminDetails, GetAdminDetailsResponse, UserQuery } from "../../users/userApi"
import { QueryErrorBoundary } from "../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner"
import { PageHeader } from "../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { Page } from "../../../shared/components/pageHeader/PageHeaderApi"
import { AdministratorForCard } from "../components/AdministratorForCard"
import { selectAuthenticatedUser } from "../../login/loginSelectors"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { SecurityCard } from "../components/SecurityCard"
import { UserTypeCard } from "../components/UserTypeCard"
import { ProfileCard } from "../components/ProfileCard"
import { useAppSelector } from "../../store/storeHooks"
import { ActionsCard } from "../components/ActionsCard"
import { RolesCard } from "../components/RolesCard"
import { UserType } from "../../login/loginTypes"
import { HideForCurrentUserType } from "../../accessControl/HideForCurrentUserType"

const useStyles = makeStyles()((theme) => ({
    wrap: {
        display: "grid",
        gap: "2rem",
    },
    box: {
        border: `1px solid ${theme.palette.tailwind.slate[100]}`,
        borderRadius: ".75rem",
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        display: "flex",
        flexDirection: "column",
        background: "white",
        padding: "2rem",
        width: "100%",
    },
}))
export const Profile = () => {
    return (
        <Container>
            <LocalizedStrict id="profile-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.USER_ACCOUNT}
                    title="User account"
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                >
                    <HideForCurrentUserType userTypes={[UserType.USER]}>
                        <StandardBreadcrumbs>
                            <LocalizedStrict id="profile-nav-item-home">
                                <BreadcrumbLink to={"/"}>Home</BreadcrumbLink>
                            </LocalizedStrict>
                            <LocalizedStrict id="profile-user-info">
                                <Breadcrumb>User account</Breadcrumb>
                            </LocalizedStrict>
                        </StandardBreadcrumbs>
                    </HideForCurrentUserType>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <ProfileContent />
                </Suspense>
            </QueryErrorBoundary>
        </Container>
    )
}

const transformAdminData = (data: GetAdminDetailsResponse) => data.admin

export const ProfileContent = () => {
    const { classes } = useStyles()
    const user = useAppSelector(selectAuthenticatedUser)

    if (!user) {
        return null
    }

    const { data: existingAdmin } = useApiQuery(getAdminDetails, {
        queryName: UserQuery.ADMIN_DETAILS,
        dependencies: {
            userId: user.id,
        },
        select: transformAdminData,
    })

    if (!existingAdmin) {
        return null
    }

    return (
        <Grid container spacing="1.5rem" alignItems="flex-start" columns={{ xs: 8, md: 12 }}>
            <Grid item xs={8} md={7} className={classes.wrap}>
                <Card className={classes.box}>
                    <ProfileCard user={existingAdmin.user} />
                </Card>
                {existingAdmin?.administratorFor &&
                    (existingAdmin.administratorFor?.company ? (
                        <Stack direction="row" justifyContent="space-between" spacing="2rem">
                            <Card className={classes.box}>
                                <AdministratorForCard
                                    list={existingAdmin.administratorFor}
                                    type={existingAdmin.type}
                                    hostedByKey="userGroup"
                                />
                            </Card>
                            <Card className={classes.box}>
                                <AdministratorForCard
                                    list={existingAdmin.administratorFor}
                                    type={existingAdmin.type}
                                    hostedByKey="company"
                                />
                            </Card>
                        </Stack>
                    ) : (
                        <Card className={classes.box}>
                            <AdministratorForCard
                                list={existingAdmin.administratorFor}
                                type={existingAdmin.type}
                                isWithIcon
                            />
                        </Card>
                    ))}
                <Card className={classes.box}>
                    <ActionsCard id={user.id} type={user.userType} />
                </Card>
            </Grid>

            <Grid item xs={8} md={5} className={classes.wrap}>
                <Card className={classes.box}>
                    <UserTypeCard type={existingAdmin.type} />
                </Card>
                <Card className={classes.box}>
                    <SecurityCard securitySettings={existingAdmin?.securitySettings} />
                </Card>
                <Card className={classes.box}>
                    <RolesCard roles={existingAdmin?.roles} />
                </Card>
            </Grid>
        </Grid>
    )
}
